import React from 'react';
import type { DateRange as CalendarRange } from 'react-day-picker';
import { LuCalendar, LuCheck } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/utils/cn';

import {
    applyManualEdit,
    DateRange,
    formatManualDate,
    formatRangeLabel,
    matchPreset,
    presetRange,
    RANGE_PRESETS,
} from './date-ranges';

interface ReportingDateRangePickerProps {
    value: DateRange;
    onChange: (range: DateRange) => void;
}

export const ReportingDateRangePicker = ({ value, onChange }: ReportingDateRangePickerProps) => {
    const [open, setOpen] = React.useState(false);
    const activePreset = matchPreset(value);

    // The input drafts and the calendar's displayed month are all derived from `value`. They live
    // in local state (drafts so partial typing isn't committed per keystroke; month so it can be
    // scrolled), but every re-sync happens in ONE place below rather than per handler.
    const startLabel = formatManualDate(value.fromInclusive);
    const endLabel = formatManualDate(value.toInclusive);
    const [startText, setStartText] = React.useState(startLabel);
    const [endText, setEndText] = React.useState(endLabel);
    const [month, setMonth] = React.useState<Date>(value.fromInclusive);

    // Whenever `value` changes — a preset, a committed typed edit, a calendar pick, or an external
    // URL change — re-seed the drafts and scroll the calendar to whichever edge moved, adjusting
    // state during render per React's "adjusting state when a prop changes" pattern. Comparing the
    // formatted labels (not object identity) is deliberate: `useReportingDateRange` returns a
    // fresh `value` object every render, so identity would loop; the labels are stable per date.
    const [syncedLabels, setSyncedLabels] = React.useState({ startLabel, endLabel });
    if (syncedLabels.startLabel !== startLabel || syncedLabels.endLabel !== endLabel) {
        setSyncedLabels({ startLabel, endLabel });
        setStartText(startLabel);
        setEndText(endLabel);
        // Prefer the start when both edges move (e.g. a preset); otherwise follow the edge edited.
        setMonth(syncedLabels.startLabel !== startLabel ? value.fromInclusive : value.toInclusive);
    }

    const selectPreset = (id: (typeof RANGE_PRESETS)[number]['id']) => {
        onChange(presetRange(id));
        setOpen(false);
    };

    // react-day-picker range mode: commit only once both ends are chosen.
    const handleSelect = (range: CalendarRange | undefined) => {
        if (range?.from && range?.to) {
            onChange({ fromInclusive: range.from, toInclusive: range.to });
        }
    };

    // Commit a typed edge on blur/Enter: apply if parseable and in order, else revert the draft to
    // the current value so the field never shows an uncommitted/invalid string. A successful edit
    // flows back through `value`, which re-seeds the drafts and scrolls the calendar above.
    const commitEdge = (edge: 'from' | 'to', text: string) => {
        const next = applyManualEdit(value, edge, text);
        if (next) {
            onChange(next);
        } else if (edge === 'from') {
            setStartText(startLabel);
        } else {
            setEndText(endLabel);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={open} className="font-normal">
                    <LuCalendar className="mr-2 h-4 w-4 shrink-0" />
                    {formatRangeLabel(value)}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="flex w-auto p-0" align="end">
                <Command className="w-48 rounded-none border-r">
                    <CommandList>
                        <CommandGroup>
                            {RANGE_PRESETS.map((preset) => (
                                <CommandItem key={preset.id} value={preset.id} onSelect={() => selectPreset(preset.id)}>
                                    <LuCheck
                                        className={cn(
                                            'mr-2 h-4 w-4',
                                            activePreset === preset.id ? 'opacity-100' : 'opacity-0',
                                        )}
                                    />
                                    {preset.label}
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
                <div className="flex flex-col">
                    <div className="flex items-center gap-2 border-b p-3">
                        <Input
                            aria-label="Start date"
                            className="h-8"
                            placeholder="e.g. Jul 22, 2026"
                            value={startText}
                            onChange={(event) => setStartText(event.target.value)}
                            onBlur={() => commitEdge('from', startText)}
                            onKeyDown={(event) => event.key === 'Enter' && commitEdge('from', startText)}
                        />
                        <span className="text-cocoa-400">–</span>
                        <Input
                            aria-label="End date"
                            className="h-8"
                            placeholder="e.g. Jul 22, 2026"
                            value={endText}
                            onChange={(event) => setEndText(event.target.value)}
                            onBlur={() => commitEdge('to', endText)}
                            onKeyDown={(event) => event.key === 'Enter' && commitEdge('to', endText)}
                        />
                    </div>
                    <Calendar
                        mode="range"
                        numberOfMonths={2}
                        month={month}
                        onMonthChange={setMonth}
                        selected={{ from: value.fromInclusive, to: value.toInclusive }}
                        onSelect={handleSelect}
                    />
                </div>
            </PopoverContent>
        </Popover>
    );
};
